/**
 * Table column definitions for the claims dashboard.
 */
import React from 'react';
import { getClientVisibleStatus } from '../../claimsUtils';
import { trackCN } from '../../../../../services/helpers';
import { claimRoleTypes } from './claimRoleConfig';

let currentClaimsDashboardRoleRef = { value: 'CLAIM_DESK' };
let reasonListRef = { value: [] };
let claimActionHandlersRef = { value: {} };

export const setColumnRefs = (roleRef, reasonRef, handlersRef) => {
  currentClaimsDashboardRoleRef = roleRef;
  reasonListRef = reasonRef;
  claimActionHandlersRef = handlersRef;
};

const buildColumn = (Header, accessor, headerClassName, className, Cell) => {
  const column = {
    Header,
    headerClassName,
    accessor,
    className
  };

  if (Cell) {
    column.Cell = Cell;
  }

  return column;
};

const allColumns = {
  cnNumber: buildColumn('CN Number', 'cnNumber', 'flex-12', 'flex-12', obj => {
    const cn =
      obj.original &&
      (obj.original.cnote || obj.original.cnNumber || obj.original.cn);
    return (
      <div className="l-row a-center">
        <div
          className="cn-highlight text-wrap-ellipsis claims-inline-351"
          title={cn}
          onClick={() => {
            const normalizedRow = {
              ...obj.original,
              cnote: cn
            };
            trackCN(normalizedRow);
          }}
        >
          {cn || '--'}
        </div>
      </div>
    );
  }),
  clientCode: buildColumn('Client', 'client', 'flex-10', 'flex-10', obj => {
    const rawData = obj.original || {};
    let val =
      rawData.clientName ||
      rawData.clientCode ||
      rawData.customerCode ||
      rawData.clientId ||
      rawData.client ||
      (rawData.rawData &&
        (rawData.rawData.clientName ||
          rawData.rawData.clientCode ||
          rawData.rawData.customerCode ||
          rawData.rawData.clientId ||
          rawData.rawData.client)) ||
      '-';
    return <span>{val}</span>;
  }),
  orgCode: buildColumn('Org Code', 'orgCode', 'flex-10', 'flex-10'),
  rpName: buildColumn('RP Name', 'rpName', 'flex-12', 'flex-12'),
  rpCode: buildColumn('RP Code', 'rpCode', 'flex-10', 'flex-10'),
  reason: buildColumn('Reason', 'reason', 'flex-18', 'flex-18', obj => {
    const rawData = obj.original || {};
    const reasonId = rawData.reason || rawData.reasonId;
    const foundReason = reasonListRef.value.find(
      r => r && (r.id === reasonId || r.id === parseInt(reasonId, 10))
    );
    return (
      <div>{foundReason ? foundReason.reason : rawData.reason || '-'}</div>
    );
  }),
  requestedAmount: buildColumn(
    'Requested Amount',
    'requestedAmount',
    'flex-12',
    'flex-12'
  ),
  approvedAmount: buildColumn(
    'Amount Approved',
    'approvedAmount',
    'flex-12',
    'flex-12'
  ),
  claimAction: buildColumn('Claim Action', 'claimAction', 'flex-12', 'flex-12'),
  recoverable: buildColumn('Recoverable', 'recoverable', 'flex-10', 'flex-10'),
  clmRmAck: buildColumn(
    'CLM/RM Acknowledgement',
    'clmRmAck',
    'flex-12',
    'flex-12',
    obj => {
      const val =
        obj && obj.original && obj.original.rawData
          ? obj.original.rawData.clmRmAck
          : null;
      return <span>{val ? 'Yes' : '-'}</span>;
    }
  ),
  requestedDate: buildColumn(
    'Requested Date',
    'raisedDate',
    'flex-12',
    'flex-12',
    obj => {
      const ts =
        (obj.original &&
          (obj.original.raisedDate || obj.original.requestedDate)) ||
        null;
      if (!ts) return <span>-</span>;
      const d = new Date(ts);
      return (
        <span>
          {d.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })}
        </span>
      );
    }
  ),
  lastUpdated: buildColumn(
    'Last Updated On',
    'lastUpdated',
    'flex-12',
    'flex-12'
  ),
  ackDate: buildColumn('Ack Date', 'ackDate', 'flex-10', 'flex-10'),
  closureDate: buildColumn('Closure Date', 'closureDate', 'flex-10', 'flex-10'),
  rcaSubmittedDate: buildColumn(
    'RCA Submitted',
    'rcaSubmittedDate',
    'flex-12',
    'flex-12'
  ),
  rcaValidatedDate: buildColumn(
    'RCA Validated',
    'rcaValidatedDate',
    'flex-12',
    'flex-12'
  ),
  approvedRejectedDate: buildColumn(
    'Approved/Rejected',
    'approvedRejectedDate',
    'flex-12',
    'flex-12'
  ),
  responsibleParties: buildColumn(
    'Responsible Parties',
    'responsibleParties',
    'flex-18',
    'flex-18'
  ),
  creditNoteNumber: buildColumn(
    'Credit Note',
    'creditNoteNumber',
    'flex-10',
    'flex-10'
  ),
  clientInstructions: buildColumn(
    'Client Instructions',
    'clientInstructions',
    'flex-15',
    'flex-15'
  ),
  status: buildColumn('Status', 'status', 'flex-18', 'flex-18', obj => {
    const rawData =
      obj && obj.original && obj.original.rawData ? obj.original.rawData : {};
    const status =
      rawData.status || (obj && obj.original && obj.original.status) || '-';

    if (status === '-') return <span>-</span>;

    const style = { color: '#dd712f', fontWeight: 600 };

    if (
      currentClaimsDashboardRoleRef.value === claimRoleTypes.CLIENT_USER ||
      currentClaimsDashboardRoleRef.value === claimRoleTypes.RP_USER
    ) {
      const friendlyStatus =
        rawData.displayStatus ||
        (obj && obj.original && obj.original.displayStatus) ||
        getClientVisibleStatus(status, rawData.isRework) ||
        '';
      const upperFriendly = friendlyStatus.toUpperCase();
      return <span style={style}>{upperFriendly}</span>;
    }

    if (
      currentClaimsDashboardRoleRef.value === claimRoleTypes.GROUND_DEPS &&
      (status === 'UNDER_INVESTIGATION' || status === 'RCA_PENDING')
    ) {
      const txt = rawData.isRework ? 'RCA PENDING (REWORK)' : 'RCA PENDING';
      return <span style={style}>{txt}</span>;
    }

    const upperStatus = status.replace(/_/g, ' ').toUpperCase();
    return <span style={style}>{upperStatus}</span>;
  }),
  sla: buildColumn('SLA', 'sla', 'flex-12', 'flex-12', obj => {
    const row = obj && obj.original;
    const rawData = (row && row.rawData) || {};
    const { slaRemainingMillis, slaStatus } = rawData;

    if (slaRemainingMillis === undefined || slaRemainingMillis === null) {
      return <span>-</span>;
    }

    const colorMap = {
      ON_TRACK: '#16A34A',
      WARNING: '#dd712f',
      BREACHED: '#DC2626'
    };
    const bgMap = {
      ON_TRACK: '#f0fdf4',
      WARNING: '#fffbeb',
      BREACHED: '#fef2f2'
    };

    let resolvedStatus = slaStatus;
    if (!resolvedStatus) {
      if (slaRemainingMillis < 0) resolvedStatus = 'BREACHED';
      else if (slaRemainingMillis < 24 * 60 * 60 * 1000)
        resolvedStatus = 'WARNING';
      else resolvedStatus = 'ON_TRACK';
    }

    const color = colorMap[resolvedStatus] || '#16A34A';
    const isOverdue = slaRemainingMillis < 0;
    const absMillis = Math.abs(slaRemainingMillis);
    const totalMinutes = Math.floor(absMillis / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const mins = totalMinutes % 60;

    const daysStr = days > 0 ? `${days} D ` : '';
    const hoursStr = `${hours} Hr `;
    const minsStr = `${mins} Min`;
    const timeText = `${isOverdue ? '-' : ''}${daysStr}${hoursStr}${minsStr}`;

    return (
      <span style={{ color, fontWeight: 600, fontSize: '13px' }}>
        {timeText}
      </span>
    );
  }),
  claimType: buildColumn(
    'Claim Type',
    'claimType',
    'flex-10',
    'flex-10',
    obj => (
      <span>{obj.original ? obj.original.claimAction || 'COF' : 'COF'}</span>
    )
  ),
  checkbox: {
    Header: () => (
      <input
        type="checkbox"
        checked={
          claimActionHandlersRef.value.isAllSelected
            ? claimActionHandlersRef.value.isAllSelected()
            : false
        }
        onChange={() => {
          if (claimActionHandlersRef.value.toggleAllSelection) {
            claimActionHandlersRef.value.toggleAllSelection();
          }
        }}
      />
    ),

    accessor: 'checkbox',
    headerClassName: 'flex-5',
    className: 'flex-5',
    sortable: false,
    Cell: obj => {
      const rowData = obj && obj.original;
      return (
        <input
          type="checkbox"
          checked={
            claimActionHandlersRef.value.isRowSelected
              ? claimActionHandlersRef.value.isRowSelected(rowData)
              : false
          }
          onChange={() => {
            if (claimActionHandlersRef.value.toggleRowSelection) {
              claimActionHandlersRef.value.toggleRowSelection(rowData);
            }
          }}
        />
      );
    }
  }
};

export const getColumnsForRole = claimRole => {
  switch (claimRole) {
    case claimRoleTypes.CLIENT_USER:
    case claimRoleTypes.RP_USER:
      return [
        allColumns.cnNumber,
        allColumns.reason,
        allColumns.requestedAmount,
        allColumns.approvedAmount,
        allColumns.requestedDate,
        allColumns.lastUpdated,
        allColumns.status
      ];

    case claimRoleTypes.SAM_KAM:
      return [
        allColumns.cnNumber,
        allColumns.reason,
        allColumns.requestedAmount,
        allColumns.approvedAmount,
        allColumns.clientCode,
        allColumns.rpName,
        allColumns.requestedDate,
        allColumns.lastUpdated,
        allColumns.status,
        allColumns.sla
      ];

    case claimRoleTypes.ZOOM_CLM:
      return [
        allColumns.cnNumber,
        allColumns.reason,
        allColumns.requestedAmount,
        allColumns.approvedAmount,
        allColumns.requestedDate,
        allColumns.lastUpdated,
        allColumns.status,
        allColumns.sla
      ];

    case claimRoleTypes.GROUND_DEPS:
      return [
        allColumns.cnNumber,
        allColumns.reason,
        allColumns.requestedAmount,
        allColumns.approvedAmount,
        allColumns.requestedDate,
        allColumns.lastUpdated,
        allColumns.status,
        allColumns.sla
      ];

    case claimRoleTypes.HQ_DEPS:
      return [
        allColumns.cnNumber,
        allColumns.reason,
        allColumns.requestedAmount,
        allColumns.approvedAmount,
        allColumns.clientCode,
        allColumns.requestedDate,
        allColumns.lastUpdated,
        allColumns.status,
        allColumns.sla
      ];

    case claimRoleTypes.FINANCE:
      return [
        allColumns.cnNumber,
        allColumns.reason,
        allColumns.requestedAmount,
        allColumns.approvedAmount,
        allColumns.clientCode,
        allColumns.requestedDate,
        allColumns.lastUpdated,
        allColumns.status,
        allColumns.sla
      ];

    case claimRoleTypes.CLAIM_DESK:
      return [
        allColumns.cnNumber,
        allColumns.reason,
        allColumns.requestedAmount,
        allColumns.approvedAmount,
        allColumns.clientCode,
        allColumns.requestedDate,
        allColumns.lastUpdated,
        allColumns.status,
        allColumns.sla
      ];

    case claimRoleTypes.HOD_DESK:
      return [
        allColumns.checkbox,
        allColumns.cnNumber,
        allColumns.reason,
        allColumns.requestedAmount,
        allColumns.approvedAmount,
        allColumns.clientCode,
        allColumns.requestedDate,
        allColumns.lastUpdated,
        allColumns.claimType,
        allColumns.status,
        allColumns.sla
      ];

    case claimRoleTypes.OPS_HOD:
    case claimRoleTypes.SALES_HOD:
      return [
        allColumns.cnNumber,
        allColumns.reason,
        allColumns.requestedAmount,
        allColumns.approvedAmount,
        allColumns.clientCode,
        allColumns.requestedDate,
        allColumns.lastUpdated,
        allColumns.status,
        allColumns.sla
      ];

    default:
      return [
        allColumns.cnNumber,
        allColumns.clientCode,
        allColumns.reason,
        allColumns.status,
        allColumns.requestedAmount,
        allColumns.approvedAmount,
        allColumns.claimAction,
        allColumns.recoverable,
        allColumns.requestedDate,
        allColumns.ackDate,
        allColumns.closureDate,
        allColumns.approvedRejectedDate,
        allColumns.responsibleParties,
        allColumns.clientInstructions,
        allColumns.creditNoteNumber,
        allColumns.sla
      ];
  }
};
